import { IDepartureArrival, IEventInfoBlockWithoutType } from "Bands/types";
import { IStageInfoBlock, IVenueInfoBlock } from "Venues/types";
import { IImage } from "./image";
import { StateErrorType } from "./state";
import { IStatus } from "./styles";

export enum IAPIState {
  idle,
  pending,
  succeeded,
  failed,
}

export interface IAddress extends IBasicAddress {
  latitude: number;
  longitude: number;
  staticImage?: string;
}

export interface IBasicAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zip: string;
  state?: string;
  country: string;
}

export interface IBaseInfoBlock {
  id: string;
  type?: string; // make compatible with venue info blocks
  text?: string | null;
  contacts?: IContact[];
  places?: IPlace[];
  files?: IFile[];
  images?: IImageItem[];
}

export type IInfoBlock = IStageInfoBlock | IVenueInfoBlock | IEventInfoBlockWithoutType;

export enum IInfoBlockAPIOrigin {
  CreateModal,
  EditModal,
  DeleteModal,
  ChooseModal,
  Unlink,
  Form,
}

export interface IContact {
  id: string;
  firstname?: string;
  lastname?: string;
  company?: string;
  jobTitle?: string;
  email?: string;
  phone?: IPhone;
  mobile?: IPhone;
}

export interface IPhoneNumber {
  id: string;
  phoneNumber?: IPhone;
  note?: string;
}

export interface IPlace {
  id: string;
  title: string;
  address: IAddress;
}

export interface IFileUpload {
  url?: string;
  description?: string;
}

export interface IFile extends IFileUpload {
  id: string;
  updatedAt?: string;
  size?: number;
  extension?: IExtension;
  source?: string;
  title: string;
  description?: string;
  user?: string;
}

export interface IImageItem extends IImage {
  id: string;
  description?: string;
  file?: IFileUpload;
}

export enum IInfoBlockItem {
  place = "place",
  contact = "contact",
  file = "file",
  image = "image",
}

export type IInfoBlockItemType = IContact | IPlace | IFile | IImageItem;

export enum IExtension {
  jpeg = "jpeg",
  jpg = "jpg",
  png = "png",
  pdf = "pdf",
  zip = "zip",
  doc = "doc",
  docx = "docx",
  xls = "xls",
  xlsx = "xlsx",
  ppt = "ppt",
  pptx = "pptx",
}

export interface IMarker {
  id: string;
  coordinates: ICoordinate;
}

export interface ITravelMarker extends IMarker {
  departure: IDepartureArrival;
  arrival: IDepartureArrival;
}

export interface IAction {
  label?: string | JSX.Element;
  icon?: React.ComponentType<any>;
  iconComponent?: JSX.Element;
  actionIcon?: React.ComponentType<any>;
  subLabel?: string;
  handleClick?: (args?: any) => void;
}

export interface IMessage {
  title?: string;
  body?: string;
  icon?: React.ComponentType<any>;
  status?: IStatus;
  "data-testid"?: string;
}

export interface IErrorPayload {
  error: StateErrorType;
  message?: IMessage;
}

export interface ICoordinate {
  longitude: number;
  latitude: number;
}

export interface IPhone {
  code: string;
  number?: string;
}

export enum IUnitType {
  metric = "metric",
  imperial = "imperial",
}

export enum ILocalStorageKey {
  unitType = "unitType",
  hideVenueEdit = "hideVenueEdit",
  cookiePreference = "cookiePreference",
  hideBandEdit = "hideBandEdit",
  lastBand = "lastBand",
  calendarView = "calendarView",
  eventTimezone = "eventTimezone",
  scheduleBlockFilter = "scheduleBlockFilter",
  profileHelpBlock = "profileHelpBlock",
  xlsExportPreferencesPerBand = "xlsExportPreferencesPerBand",
}

export enum IPermissionKey {
  edit = "EDIT",
  delete = "DELETE",
  addUser = "ADD_USER",
  viewFullProfile = "VIEW_FULL_PROFILE",
  requestPublish = "REQUEST_PUBLISH",
  publish = "PUBLISH",
  unPublish = "UNPUBLISH",
  createEvent = "CREATE_EVENT",
  createPeopleBlock = "CREATE_PEOPLE_BLOCK",
  createScheduleBlock = "CREATE_SCHEDULE_BLOCK",
  createVenueBlock = "CREATE_VENUE_BLOCK",
  createGuestlistBlock = "CREATE_GUESTLIST_BLOCK",
  createTravelBlock = "CREATE_TRAVEL_BLOCK",
  createAccommodationBlock = "CREATE_ACCOMMODATION_BLOCK",
  viewPassenger = "VIEW_PASSENGERS",
  createBlock = "CREATE_BLOCK",
  viewFullSchedule = "VIEW_FULL_SCHEDULE",
  addGuest = "ADD_GUEST",
  changeEntryStatus = "CHANGE_ENTRY_STATUS",
  manageAllGuests = "MANAGE_ALL_GUESTS",
  download = "DOWNLOAD",
  cancel = "CANCEL",
  manageSubscriptions = "MANAGE_SUBSCRIPTIONS",
  changePlan = "CHANGE_PLAN",
}
export interface IPermissionForKey {
  granted: boolean;
  permission: IPermissionKey;
}

export enum PermissionType {
  "create" = "create",
  "edit" = "edit",
  "view" = "view",
}

interface IBasePermissionType {
  id: string;
  label: string;
  shortLabel: string;
  description?: string;
  type: PermissionType;
}

export interface IPermissionType extends IBasePermissionType {
  locked?: boolean;
  permissionGroup?: IPermissionGroup | null;
  dependencies?: { id: string }[];
}

export interface IPermissionGroup extends IBasePermissionType {
  parentGroup?: IPermissionGroup | null;
}

export interface IPermission {
  id: string;
  permission: IPermissionType;
  granted: boolean;
}

export interface IPagination {
  currentPage: number;
  totalPages: number;
}

export interface IPager extends IPagination {
  onPageChange: (newPage: number) => void;
}

export enum ICookiePreferenceType {
  acceptAll = "acceptAll",
  denyAnalytical = "denyAnalytical",
}

export enum ISubDomain {
  bands = "app",
  venues = "venues",
}

export enum ISex {
  m = "m",
  f = "f",
  x = "x",
}

export interface IDate {
  day?: number;
  shortWeekday?: string;
  weekday?: string;
  month: number;
  year: number;
}

export interface DateChangeProps {
  date?: string;
  time?: string;
  timeZone?: string;
}
